import { credencialActual } from '@nucleo/sesion'

export class ErrorDeApi extends Error {
  readonly estado: number
  readonly codigo: string
  readonly detalle: unknown

  constructor(estado: number, codigo: string, mensaje: string, detalle?: unknown) {
    super(mensaje)
    this.estado = estado
    this.codigo = codigo
    this.detalle = detalle
  }
}

interface CuerpoDeError {
  codigo?: string
  mensaje?: string
  detalle?: unknown
}

/** Mensaje comprensible cuando el servidor no envía uno propio (nunca «Error 422» a secas). */
function mensajePorEstado(estado: number): string {
  if (estado === 422) return 'Revisa los datos: algún campo está vacío, es demasiado largo o tiene un formato no válido.'
  if (estado === 403) return 'Tu rol no permite esta acción en la organización.'
  if (estado === 404) return 'No encontramos este elemento; puede haber sido eliminado. Actualiza la página.'
  if (estado === 409) return 'Otro cambio llegó antes que el tuyo. Actualiza la página y vuelve a intentarlo.'
  if (estado === 429) return 'Se alcanzó el límite de uso por ahora. Espera un momento antes de reintentar.'
  if (estado >= 500) return 'El servidor no pudo completar la operación. Inténtalo nuevamente en unos minutos.'
  return `No fue posible completar la operación (código ${estado}).`
}

async function leerError(respuesta: Response): Promise<ErrorDeApi> {
  let cuerpo: CuerpoDeError = {}
  try {
    cuerpo = (await respuesta.json()) as CuerpoDeError
  } catch {
    /* sin cuerpo JSON */
  }
  return new ErrorDeApi(respuesta.status, cuerpo.codigo ?? `http_${respuesta.status}`, cuerpo.mensaje ?? mensajePorEstado(respuesta.status), cuerpo.detalle)
}

export async function solicitar<T>(metodo: 'GET' | 'POST' | 'PUT' | 'DELETE', ruta: string, cuerpo?: unknown): Promise<T> {
  const cabeceras: Record<string, string> = { Accept: 'application/json' }
  const credencial = credencialActual()
  if (credencial) cabeceras.Authorization = `Bearer ${credencial}`
  if (cuerpo !== undefined) cabeceras['Content-Type'] = 'application/json'
  const opciones: RequestInit = { method: metodo, headers: cabeceras, credentials: 'same-origin' }
  if (cuerpo !== undefined) opciones.body = JSON.stringify(cuerpo)
  const respuesta = await fetch(ruta, opciones)
  if (!respuesta.ok) throw await leerError(respuesta)
  if (respuesta.status === 204) return undefined as T
  return (await respuesta.json()) as T
}

export async function subirArchivo<T>(ruta: string, datos: FormData): Promise<T> {
  const credencial = credencialActual()
  const cabeceras: Record<string, string> = { Accept: 'application/json' }
  if (credencial) cabeceras.Authorization = `Bearer ${credencial}`
  const respuesta = await fetch(ruta, { method: 'POST', headers: cabeceras, body: datos, credentials: 'same-origin' })
  if (!respuesta.ok) throw await leerError(respuesta)
  return (await respuesta.json()) as T
}

export async function obtenerBlob(ruta: string): Promise<Blob> {
  const credencial = credencialActual()
  const cabeceras: Record<string, string> = {}
  if (credencial) cabeceras.Authorization = `Bearer ${credencial}`
  const respuesta = await fetch(ruta, { headers: cabeceras, credentials: 'same-origin' })
  if (!respuesta.ok) throw await leerError(respuesta)
  return respuesta.blob()
}

export async function descargar(ruta: string, nombre: string): Promise<void> {
  const cabeceras: Record<string, string> = {}
  const credencial = credencialActual()
  if (credencial) cabeceras.Authorization = `Bearer ${credencial}`
  const respuesta = await fetch(ruta, { headers: cabeceras, credentials: 'same-origin' })
  if (!respuesta.ok) throw await leerError(respuesta)
  const blob = await respuesta.blob()
  const url = URL.createObjectURL(blob)
  const enlace = document.createElement('a')
  enlace.href = url
  enlace.download = nombre
  document.body.appendChild(enlace)
  enlace.click()
  enlace.remove()
  URL.revokeObjectURL(url)
}
